import {t} from '@lingui/macro';
import _, {difference} from 'lodash';

import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {buildContactForHandle, ContactSourcesEnum} from '../../../models/api/contactModel';
import {RecipientRolesEnum} from '../../../models/api/recipientModel';
import {CellsSortDirectionsEnum} from '../../../models/api/sortOptionModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {findPanelSequence} from '../../../services/panels';
import {InviteTeammatePopoverAccessPointsEnum} from '../../../services/tracking';
import {batchStore} from '../../appBatching';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getConversations} from '../../data/dataSelectors';
import {
  composeMessageAction,
  navigateStandaloneComposerAction,
} from '../../data/messages/draftMessagesActions';
import {getLocalize} from '../../stateless/internationalizationSelectors';
import {navigateAnalyticsAction} from '../analytics/analyticsPanelActions';
import {navigateContactsManagerAction} from '../contacts/contactsManager/contactsManagerPanelActions';
import {navigateInboxesAsyncAction} from '../inboxes/inboxesPanelActions';
import {normalInboxesSelection} from '../inboxes/parameters/inboxesParametersHelpers';
import {getDefaultInboxesParameters} from '../inboxes/parameters/inboxesParametersSelectors';
import {navigateLegacyAction} from '../legacy/legacyPanelActions';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {getPanels, makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {
  AppPanelId,
  makeAnalyticsPanelId,
  makeComposerPanelId,
  makeContactsManagerPanelId,
  makeInboxesPanelId,
  makeLegacyPanelId,
  makeViewerPanelId,
  NewPanelId,
  PanelTypesEnum,
} from '../panelStateBase';
import {navigateViewerAsyncAction} from '../viewer/viewerPanelActions';
import {NavigationBlockerId} from './appPanelHelpers';
import {getActiveHelpChatConfig} from './appPanelSelectors';
import {BannersAction} from './banners/bannersActions';
import {AppContentTypesEnum, AppParameters, defaultPathsByContentType} from './parameters/appParameters';

/*
 * Action types.
 */

export enum AppPanelActionsEnum {
  NAVIGATED = '@@panels/app/NAVIGATED',
  NAVIGATION_BLOCKED = '@@panels/app/NAVIGATION_BLOCKED',
  NAVIGATION_CANCELED = '@@panels/app/NAVIGATION_CANCELED',
  NAVIGATION_BLOCKER_REGISTERED = '@@panels/app/NAVIGATION_BLOCKER_REGISTERED',
  NAVIGATION_BLOCKER_UNREGISTERED = '@@panels/app/NAVIGATION_BLOCKER_UNREGISTERED',
  OPEN_HELP_CHAT = '@@panels/app/OPEN_HELP_CHAT',
  OPEN_DEMO_INBOX_LOADING_MODAL = '@@panels/app/OPEN_DEMO_INBOX_LOADING_MODAL',
  CLOSE_DEMO_INBOX_LOADING_MODAL = '@@panels/app/CLOSE_DEMO_INBOX_LOADING_MODAL',
  OPEN_INVITE_TEAMMATE_POPOVER = '@@panels/app/OPEN_INVITE_TEAMMATE_POPOVER',
  CLOSE_INVITE_TEAMMATE_POPOVER = '@@panels/app/CLOSE_INVITE_TEAMMATE_POPOVER',
}

/*
 * Types.
 */

type AppPanelActionPayload = PanelActionPayload<AppPanelId>;

interface AppNavigatedActionPayload extends AppPanelActionPayload {
  contentType: AppContentTypesEnum;
  /** Used by the app navigation <-> browser history middleware only. */
  navigationShouldReplace: boolean;
}

interface AppNavigatedAction extends ActionWithPayload<AppNavigatedActionPayload> {
  type: AppPanelActionsEnum.NAVIGATED;
}

interface AppNavigationBlockedActionPayload extends AppPanelActionPayload {
  parameters: AppParameters;
}

interface AppNavigationBlockedAction extends ActionWithPayload<AppNavigationBlockedActionPayload> {
  type: AppPanelActionsEnum.NAVIGATION_BLOCKED;
}

interface AppNavigationCanceledAction extends ActionWithPayload<AppPanelActionPayload> {
  type: AppPanelActionsEnum.NAVIGATION_CANCELED;
}

interface AppNavigationBlockerActionPayload extends AppPanelActionPayload {
  blockerId: NavigationBlockerId;
}

interface AppNavigationBlockerRegisteredAction extends ActionWithPayload<AppNavigationBlockerActionPayload> {
  type: AppPanelActionsEnum.NAVIGATION_BLOCKER_REGISTERED;
}

interface AppNavigationBlockerUnregisteredAction
  extends ActionWithPayload<AppNavigationBlockerActionPayload> {
  type: AppPanelActionsEnum.NAVIGATION_BLOCKER_UNREGISTERED;
}

interface OpenHelpChatAction extends ActionWithPayload<AppPanelActionPayload> {
  type: AppPanelActionsEnum.OPEN_HELP_CHAT;
}

interface OpenDemoInboxLoadingModalAction extends ActionWithPayload<AppPanelActionPayload> {
  type: AppPanelActionsEnum.OPEN_DEMO_INBOX_LOADING_MODAL;
}

interface CloseDemoInboxLoadingModalAction extends ActionWithPayload<AppPanelActionPayload> {
  type: AppPanelActionsEnum.CLOSE_DEMO_INBOX_LOADING_MODAL;
}

interface OpenInviteTeammatePopoverActionPaylaod extends AppPanelActionPayload {
  accessPoint: InviteTeammatePopoverAccessPointsEnum;
}

interface OpenInviteTeammatePopoverAction extends ActionWithPayload<OpenInviteTeammatePopoverActionPaylaod> {
  type: AppPanelActionsEnum.OPEN_INVITE_TEAMMATE_POPOVER;
}

interface CloseInviteTeammatePopoverAction extends ActionWithPayload<AppPanelActionPayload> {
  type: AppPanelActionsEnum.CLOSE_INVITE_TEAMMATE_POPOVER;
}

/*
 * Action creators.
 */

type NavigateAppOptions = {
  navigationShouldReplace: boolean;
  ignoreNavigationBlockerId?: NavigationBlockerId;
};

export function navigateAppAsyncAction(
  appPanelId: AppPanelId,
  maybeAppParameters: AppParameters | undefined,
  {navigationShouldReplace, ignoreNavigationBlockerId}: NavigateAppOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the app panel, if any.
    const panel = getPanels(getState())[appPanelId];
    if (panel && panel.type !== PanelTypesEnum.APP) {
      throw new Error('Wrong panel type.');
    }

    const appParameters: AppParameters = maybeAppParameters ?? {
      contentType: panel?.defaultContentType ?? AppContentTypesEnum.INBOXES,
    };

    // Check if some blocked navigations need to be ignored.
    const navigationBlockerIds = panel?.navigationBlockerIds || [];
    const navigationToBlock = ignoreNavigationBlockerId
      ? difference(navigationBlockerIds, [ignoreNavigationBlockerId])
      : navigationBlockerIds;
    // If something is preventing navigation, remember this navigation request for later.
    const navigationBlockerCount = navigationToBlock.length ?? 0;
    if (navigationBlockerCount > 0) {
      dispatch<AppNavigationBlockedAction>({
        type: AppPanelActionsEnum.NAVIGATION_BLOCKED,
        payload: {
          panelId: appPanelId,
          parameters: appParameters,
        },
      });
      return;
    }

    await batchStore(async () => {
      // Navigate the content panel.
      const contentPromise = dispatch(navigateAppContentAsyncAction(appPanelId, appParameters));

      // Update or create the panel.
      if (panel) {
        dispatch<AppNavigatedAction>({
          type: AppPanelActionsEnum.NAVIGATED,
          payload: {
            panelId: appPanelId,
            contentType: appParameters.contentType,
            navigationShouldReplace,
          },
        });
      } else {
        const {contentType} = appParameters;
        const defaultContentType =
          contentType !== AppContentTypesEnum.SETTINGS ? contentType : AppContentTypesEnum.INBOXES;
        dispatch(
          setPanelAction({
            id: appPanelId,
            type: PanelTypesEnum.APP,
            contentType,
            defaultContentType,
            navigationBlockerIds: [],
            banners: {},
            visibleBannerUids: [],
            undoBannerUids: [],
            errorBannerUids: [],
            helpChatOpenAttempt: 0,
            isDemoInboxLoadingModalOpen: false,
            inviteTeammatePopover: undefined,
            sequence: findPanelSequence(),
          }),
        );
      }

      await contentPromise;
    });
  };
}

function navigateAppContentAsyncAction(
  appPanelId: AppPanelId,
  appParameters: AppParameters,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    switch (appParameters.contentType) {
      case AppContentTypesEnum.INBOXES: {
        const inboxesPanelId = makeInboxesPanelId(appPanelId);
        await dispatch(navigateInboxesAsyncAction(inboxesPanelId, appParameters.parameters));
        break;
      }

      case AppContentTypesEnum.CALENDARS: {
        // TODO: Navigate the calendar here instead of using inLegacyTransientPanel().
        // const calendarsPanelId = makeCalendarsPanelId(appPanelId);
        // dispatch(navigateCalendarsAction(calendarsPanelId));
        break;
      }

      case AppContentTypesEnum.CONTACTS: {
        const contactsManagerPanelId = makeContactsManagerPanelId(appPanelId);
        dispatch(navigateContactsManagerAction(contactsManagerPanelId, appParameters.parameters));
        break;
      }

      case AppContentTypesEnum.ANALYTICS: {
        const analyticsPanelId = makeAnalyticsPanelId(appPanelId);
        dispatch(navigateAnalyticsAction(analyticsPanelId, appParameters.parameters));
        break;
      }

      case AppContentTypesEnum.VISITOR_EXPERIENCE:
      case AppContentTypesEnum.VISITOR_EXPERIENCE_V3: {
        const state = getState();
        const inboxesPanelId = makeInboxesPanelId(appPanelId);
        const defaultParameters = getDefaultInboxesParameters(state);
        // Since this is a visitor boot, we will only have 1 conversationId in the boot, So we are getting that
        // ConversationId in order to pre-load p2-p3 with that conversation.
        const allConversations = getConversations(state);
        const firstConversation = _(allConversations)
          .values()
          .compact()
          .slice(0, 1)
          .map((c) => c.id)
          .value();

        const parameters = {
          ...defaultParameters,
          selection: normalInboxesSelection(firstConversation),
          sortDirection: CellsSortDirectionsEnum.DESCENDING,
        };
        await dispatch(navigateInboxesAsyncAction(inboxesPanelId, parameters));
        break;
      }

      case AppContentTypesEnum.VISITOR_VIEWER: {
        const state = getState();
        const viewerPanelId = makeViewerPanelId(appPanelId);
        const defaultParameters = getDefaultInboxesParameters(state);
        const allConversations = getConversations(state);
        const allConversationIds = _(allConversations).values().compact().map('id').value();

        const parameters = {
          ...defaultParameters,
          selection: normalInboxesSelection(allConversationIds),
        };
        await dispatch(navigateViewerAsyncAction(viewerPanelId, parameters));
        break;
      }

      case AppContentTypesEnum.STANDALONE_COMPOSER: {
        const composerPanelId = makeComposerPanelId(appPanelId);
        dispatch(navigateStandaloneComposerAction(composerPanelId, appParameters.mailtoOptions));
        break;
      }

      case AppContentTypesEnum.SEQUENCES:
      case AppContentTypesEnum.AUDIT_TRAIL:
      case AppContentTypesEnum.SETTINGS: {
        const legacyPanelId = makeLegacyPanelId(appPanelId);
        dispatch(
          navigateLegacyAction(
            legacyPanelId,
            appParameters.path ?? defaultPathsByContentType[appParameters.contentType],
          ),
        );
        break;
      }

      default:
        throw new UnreachableError(appParameters);
    }
  };
}

export function cancelNavigationAction(panelId: NewPanelId): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});

    dispatch<AppNavigationCanceledAction>({
      type: AppPanelActionsEnum.NAVIGATION_CANCELED,
      payload: {
        panelId: appPanelId,
      },
    });
  };
}

export function registerNavigationBlockerAction(
  panelId: NewPanelId,
  blockerId: NavigationBlockerId,
): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});

    dispatch<AppNavigationBlockerRegisteredAction>({
      type: AppPanelActionsEnum.NAVIGATION_BLOCKER_REGISTERED,
      payload: {
        panelId: appPanelId,
        blockerId,
      },
    });
  };
}

export function unregisterNavigationBlockerAction(
  panelId: NewPanelId,
  blockerId: NavigationBlockerId,
): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});

    dispatch<AppNavigationBlockerUnregisteredAction>({
      type: AppPanelActionsEnum.NAVIGATION_BLOCKER_UNREGISTERED,
      payload: {
        panelId: appPanelId,
        blockerId,
      },
    });
  };
}

export function openHelpChatAction(panelId: NewPanelId): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    // If the chat is not available, open a composer instead.
    if (!getActiveHelpChatConfig(state)) {
      const localize = getLocalize(state);
      const supportRecipient: VisualRecipientModel = {
        namespace: 'global',
        role: RecipientRolesEnum.TO,
        contact: buildContactForHandle(ContactSourcesEnum.EMAIL, 'support@frontapp.com'),
      };

      dispatch(
        composeMessageAction({
          recipients: [supportRecipient],
          subject: localize(t('helpchat-emailsubject')`Front Support Request`),
        }),
      );
      return;
    }

    // Otherwise, open the chat widget.
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    const action: OpenHelpChatAction = {
      type: AppPanelActionsEnum.OPEN_HELP_CHAT,
      payload: {panelId: appPanelId},
    };
    dispatch(action);
  };
}

export function openDemoInboxLoadingModalAction(panelId: NewPanelId): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch({
      type: AppPanelActionsEnum.OPEN_DEMO_INBOX_LOADING_MODAL,
      payload: {panelId: appPanelId},
    });
  };
}

export function closeDemoInboxLoadingModalAction(panelId: AppPanelId): CloseDemoInboxLoadingModalAction {
  return {
    type: AppPanelActionsEnum.CLOSE_DEMO_INBOX_LOADING_MODAL,
    payload: {panelId},
  };
}

export function openInviteTeammatePopoverAction(
  panelId: NewPanelId,
  accessPoint: InviteTeammatePopoverAccessPointsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch({
      type: AppPanelActionsEnum.OPEN_INVITE_TEAMMATE_POPOVER,
      payload: {panelId: appPanelId, accessPoint},
    });
  };
}

export function closeInviteTeammatePopoverAction(panelId: AppPanelId): CloseInviteTeammatePopoverAction {
  return {
    type: AppPanelActionsEnum.CLOSE_INVITE_TEAMMATE_POPOVER,
    payload: {panelId},
  };
}

/*
 * Union type.
 */

export type AppPanelAction =
  | AppNavigatedAction
  | AppNavigationBlockedAction
  | AppNavigationCanceledAction
  | AppNavigationBlockerRegisteredAction
  | AppNavigationBlockerUnregisteredAction
  | OpenHelpChatAction
  | OpenDemoInboxLoadingModalAction
  | CloseDemoInboxLoadingModalAction
  | OpenInviteTeammatePopoverAction
  | CloseInviteTeammatePopoverAction
  | BannersAction;
